// ============================================================
// WorkspaceProofPanel — evidence, receipts, readiness.
//
// The proof grammar: every row is a named check or receipt with
// a status from the shared vocabulary, optional mono value,
// timestamp, and hash. Used by Activation Doctor (system proof
// board), provider surfaces (receipts), and agent routes.
//
// Honesty contract: rows render exactly what the caller proves.
// A failing check renders red; the panel never rolls failures
// up into a green summary.
//
//   rows: [{ id, label, status, value?, detail?, time?, hash?, onOpen? }]
// ============================================================

import WorkspaceStatusChip from './WorkspaceStatusChip'
import { workspaceStatusMeta, tint, WORKSPACE_FONT, WORKSPACE_SURFACE, WORKSPACE_TEXT } from './workspaceTheme'

function shortHash(v) {
  if (!v) return null
  const s = String(v)
  return s.length <= 14 ? s : `${s.slice(0, 8)}…${s.slice(-4)}`
}

export default function WorkspaceProofPanel({
  title = 'PROOF',
  rows = [],
  loading = false,
  sourceLabel,
  emptyBody = 'No receipts recorded yet.',
  footer,
  compact = false,
  style: styleOverride,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, ...styleOverride }}>
      <div
        style={{
          fontSize: 9,
          letterSpacing: '0.26em',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: WORKSPACE_TEXT.tertiary,
          fontFamily: WORKSPACE_FONT.mono,
        }}
      >
        {title}
      </div>

      {loading ? (
        <div
          style={{
            padding: '14px 0',
            fontSize: 10,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.14em',
          }}
        >
          LOADING PROOF…
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            padding: '16px 14px',
            borderRadius: 10,
            border: `1px dashed ${WORKSPACE_SURFACE.line}`,
            fontSize: 11,
            lineHeight: 1.6,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.ui,
          }}
        >
          {emptyBody}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rows.map((row) => {
            const meta = workspaceStatusMeta(row.status)
            const clickable = typeof row.onOpen === 'function'
            return (
              <div
                key={row.id ?? row.label}
                onClick={clickable ? row.onOpen : undefined}
                role={clickable ? 'button' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: compact ? '8px 10px' : '10px 12px',
                  borderRadius: 8,
                  background: WORKSPACE_SURFACE.card,
                  border: `1px solid ${WORKSPACE_SURFACE.lineFaint}`,
                  cursor: clickable ? 'pointer' : 'default',
                  minWidth: 0,
                }}
              >
                {/* status accent bar */}
                <span
                  aria-hidden
                  style={{
                    width: 3,
                    alignSelf: 'stretch',
                    borderRadius: 2,
                    background: meta.accent,
                    boxShadow: `0 0 8px ${tint(meta.accent, 25)}`,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: WORKSPACE_TEXT.primary,
                        fontFamily: WORKSPACE_FONT.ui,
                      }}
                    >
                      {row.label}
                    </span>
                    {row.value != null && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: meta.accent,
                          fontFamily: WORKSPACE_FONT.mono,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {row.value}
                      </span>
                    )}
                  </div>
                  {row.detail && (
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 11,
                        lineHeight: 1.6,
                        color: WORKSPACE_TEXT.tertiary,
                        fontFamily: WORKSPACE_FONT.ui,
                      }}
                    >
                      {row.detail}
                    </div>
                  )}
                  {(row.time || row.hash) && (
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 9,
                        color: WORKSPACE_TEXT.muted,
                        fontFamily: WORKSPACE_FONT.mono,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {[row.time, shortHash(row.hash)].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <WorkspaceStatusChip status={row.status} size="sm" style={{ flexShrink: 0 }} />
              </div>
            )
          })}
        </div>
      )}

      {sourceLabel && (
        <div
          style={{
            fontSize: 9,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.06em',
          }}
        >
          {sourceLabel}
        </div>
      )}
      {footer}
    </div>
  )
}
